// src/pages/BusinessProfile.tsx
// Business Profile page with view/edit pattern like UserProfile

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  RefreshCw,
  Loader2,
  AlertCircle,
  Edit3,
  Save,
  X,
  Camera,
  User,
  Building,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  Award,
  Shield,
  Images
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  useProviderProfile,
  useUpdateProviderProfile,
  useUploadProfileImage,
  useSetupProgress,
  type BusinessProfile,
} from '@/hooks/useProviderProfile';
import ImageUploadComponent from '@/components/common/ImageUpload/ImageUploadComponent';
import { useImageUpload } from '@/components/common/ImageUpload/ImageUploadHook';
import type { ImageFile } from '@/types/imageUpload';
import { UPLOAD_PRESETS } from '@/types/imageUpload';

const BusinessProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Hooks for data and operations
  const { data: profileData, isLoading, error, refetch } = useProviderProfile();
  const { mutate: updateProfile, isPending: isUpdating } = useUpdateProviderProfile();
  const { mutate: uploadImage, isPending: isUploading } = useUploadProfileImage();
  const { progress, isComplete, nextSteps, isFirstTime } = useSetupProgress();

  // UI state
  const [isEditing, setIsEditing] = useState(false);
  const [imageFiles, setImageFiles] = useState<ImageFile[]>([]);
  const [galleryImages, setGalleryImages] = useState<ImageFile[]>([]);
  const [avatarImages, setAvatarImages] = useState<ImageFile[]>([]);

  // Get provider data first
  const provider = profileData?.provider;

  // Gallery media management
  const galleryUpload = useImageUpload({
    entityType: 'provider',
    entityId: provider?.provider_id
  });

  // Avatar media management
  const avatarUpload = useImageUpload({
    entityType: 'provider_avatar',
    entityId: provider?.provider_id
  });

  // Form state
  const [formData, setFormData] = useState<BusinessProfile>({
    business_name: '',
    description: '',
    about_me: '',
    business_type: 'individual',
    provider_slug_url: '',
    business_phone: '',
    business_email: '',
    website: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    years_experience: 1,
    specializations: '',
    certifications: '',
    business_license: '',
    insurance_details: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    preferred_language: 'en',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auth guard
  useEffect(() => {
    if (user?.role !== 'provider') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load both gallery and avatar images
  useEffect(() => {
    const loadAllImages = async () => {
      if (provider?.provider_id) {
        try {
          // Load gallery images
          const galleryResponse = await galleryUpload.getEntityImages('provider', provider.provider_id);
          if (galleryResponse?.images) {
            const galleryImageFiles: ImageFile[] = galleryResponse.images.map((img: any) => ({
              id: String(img.media_id),
              media_id: img.media_id,
              url: img.full_url,
              caption: img.caption,
              isPrimary: img.is_primary,
              isUploaded: true,
              original_filename: img.original_filename,
              file_size: img.file_size,
              width: img.width,
              height: img.height
            }));
            setGalleryImages(galleryImageFiles);
            console.log('📸 Loaded gallery images:', galleryImageFiles.length);
          }

          // Load avatar images
          const avatarResponse = await avatarUpload.getEntityImages('provider_avatar', provider.provider_id);
          if (avatarResponse?.images) {
            const avatarImageFiles: ImageFile[] = avatarResponse.images.map((img: any) => ({
              id: String(img.media_id),
              media_id: img.media_id,
              url: img.full_url,
              caption: img.caption,
              isPrimary: img.is_primary,
              isUploaded: true,
              original_filename: img.original_filename,
              file_size: img.file_size,
              width: img.width,
              height: img.height
            }));
            setAvatarImages(avatarImageFiles);
            console.log('👤 Loaded avatar images:', avatarImageFiles.length);
          }
        } catch (error) {
          console.error('Failed to load images:', error);
        }
      }
    };

    loadAllImages();
  }, [provider?.provider_id]);

  // Refresh function to reload images after uploads/changes
  const refreshImages = useCallback(async () => {
    if (provider?.provider_id) {
      try {
        // Reload gallery images
        const galleryResponse = await galleryUpload.getEntityImages('provider', provider.provider_id);
        if (galleryResponse?.images) {
          const galleryImageFiles: ImageFile[] = galleryResponse.images.map((img: any) => ({
            id: String(img.media_id),
            media_id: img.media_id,
            url: img.full_url,
            caption: img.caption,
            isPrimary: img.is_primary,
            isUploaded: true,
            original_filename: img.original_filename,
            file_size: img.file_size,
            width: img.width,
            height: img.height
          }));
          setGalleryImages(galleryImageFiles);
          console.log('🔄 Refreshed gallery images:', galleryImageFiles.length);
        }

        // Reload avatar images
        const avatarResponse = await avatarUpload.getEntityImages('provider_avatar', provider.provider_id);
        if (avatarResponse?.images) {
          const avatarImageFiles: ImageFile[] = avatarResponse.images.map((img: any) => ({
            id: String(img.media_id),
            media_id: img.media_id,
            url: img.full_url,
            caption: img.caption,
            isPrimary: img.is_primary,
            isUploaded: true,
            original_filename: img.original_filename,
            file_size: img.file_size,
            width: img.width,
            height: img.height
          }));
          setAvatarImages(avatarImageFiles);
          console.log('🔄 Refreshed avatar images:', avatarImageFiles.length);
        }
      } catch (error) {
        console.error('Failed to refresh images:', error);
      }
    }
  }, [provider?.provider_id, galleryUpload, avatarUpload]);

  // Initialize form with profile data
  useEffect(() => {
    if (profileData?.provider) {
      const profile = profileData.provider;
      setFormData({
        business_name: profile.business_name || '',
        description: profile.description || '',
        about_me: profile.about_me || '',
        business_type: profile.business_type as any || 'individual',
        provider_slug_url: profile.provider_slug_url || '',
        business_phone: profile.business_phone || profile.phone_number || '',
        business_email: profile.business_email || '',
        website: profile.website || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        postal_code: profile.postal_code || '',
        country: profile.country || '',
        years_experience: Number(profile.years_experience) || 1,
        specializations: profile.specializations || '',
        certifications: profile.certifications || '',
        business_license: profile.business_license || '',
        insurance_details: profile.insurance_details || '',
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone_number: profile.phone_number || '',
        preferred_language: profile.preferred_language || 'en',
      });

      // Set up image files if profile image exists
      if (profile.profile_picture) {
        setImageFiles([{
          id: 'current_profile_image',
          url: `/media/serve/${profile.profile_picture}`,
          isUploaded: true,
          isPrimary: true,
          caption: 'Profile Image',
          media_id: `profile_${profile.provider_id}`
        }]);
      }
    }
  }, [profileData]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setErrors({});

    updateProfile(formData, {
      onSuccess: () => {
        setIsEditing(false);
        refetch();
      },
      onError: (error: any) => {
        if (error.validation) {
          setErrors(error.validation);
        }
      }
    });
  };

  // Handle image upload
  const handleImageUpload = async (files: ImageFile[]) => {
    try {
      for (const imageFile of files) {
        if (imageFile.file && !imageFile.isUploaded) {
          await uploadImage(imageFile.file, {
            onSuccess: (data) => {
              // Update the image file with the uploaded data
              setImageFiles(prev => prev.map(img => 
                img.id === imageFile.id 
                  ? { 
                      ...img, 
                      isUploaded: true, 
                      url: data.url,
                      media_id: String(data.media_id)
                    }
                  : img
              ));
              refetch(); // Refresh profile data to get updated profile picture
            }
          });
        }
      }
    } catch (error) {
      console.error('Image upload failed:', error);
    }
  };

  // Handle gallery upload
  const handleGalleryUpload = async (files: ImageFile[]) => {
    try {
      const fileObjects = files.filter(f => f.file).map(f => f.file!);
      if (fileObjects.length > 0) {
        await galleryUpload.uploadImages(fileObjects);
        console.log('✅ Gallery upload completed, refreshing images...');
        // Refresh both galleries to ensure consistency
        await refreshImages();
      }
    } catch (error) {
      console.error('Gallery upload failed:', error);
    }
  };

  // Handle gallery image deletion
  const handleGalleryDelete = async (imageId: string) => {
    try {
      const image = galleryImages.find(img => img.id === imageId);
      if (image?.media_id) {
        await galleryUpload.deleteImage(image.media_id);
        console.log('✅ Gallery image deleted, refreshing images...');
        await refreshImages();
      }
    } catch (error) {
      console.error('Gallery delete failed:', error);
    }
  };

  // Handle caption update
  const handleCaptionUpdate = async (imageId: string, caption: string) => {
    try {
      const image = galleryImages.find(img => img.id === imageId);
      if (image?.media_id) {
        await galleryUpload.updateCaption(image.media_id, caption);
        console.log('✅ Caption updated, refreshing images...');
        await refreshImages();
      }
    } catch (error) {
      console.error('Caption update failed:', error);
    }
  };

  // Handle set primary image
  const handleSetPrimary = async (imageId: string) => {
    try {
      const image = galleryImages.find(img => img.id === imageId);
      if (image?.media_id) {
        await galleryUpload.setPrimaryImage(image.media_id);
        console.log('✅ Primary image set, refreshing images...');
        await refreshImages();
      }
    } catch (error) {
      console.error('Set primary failed:', error);
    }
  };

  // Avatar handlers
  const handleAvatarUpload = async (files: ImageFile[]) => {
    try {
      const fileObjects = files.filter(f => f.file).map(f => f.file!);
      if (fileObjects.length > 0) {
        await avatarUpload.uploadImages(fileObjects);
        console.log('✅ Avatar upload completed, refreshing images...');
        // Refresh both galleries to ensure consistency
        await refreshImages();
      }
    } catch (error) {
      console.error('Avatar upload failed:', error);
    }
  };

  const handleAvatarDelete = async (imageId: string) => {
    try {
      const image = avatarImages.find(img => img.id === imageId);
      if (image?.media_id) {
        await avatarUpload.deleteImage(image.media_id);
        console.log('✅ Avatar deleted, refreshing images...');
        await refreshImages();
      }
    } catch (error) {
      console.error('Avatar delete failed:', error);
    }
  };

  const handleAvatarSetPrimary = async (imageId: string) => {
    try {
      const image = avatarImages.find(img => img.id === imageId);
      if (image?.media_id) {
        await avatarUpload.setPrimaryImage(image.media_id);
        console.log('✅ Primary avatar set, refreshing images...');
        await refreshImages();
        // Also refresh profile to update legacy fields
        refetch();
      }
    } catch (error) {
      console.error('Set primary avatar failed:', error);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Failed to Load Profile
          </h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="btn-primary flex items-center mx-auto"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Business Profile
              </h1>
              <p className="text-gray-600 mt-1">
                Manage your business information and professional details
              </p>
            </div>
          </div>
          
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-primary flex items-center"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Edit Profile
            </button>
          )}
        </div>

        {/* Setup Progress */}
        {!isComplete && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Profile Completion</h3>
              <span className="text-sm text-gray-500">{progress}% complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            {nextSteps.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Complete these steps:</p>
                {nextSteps.map((step, index) => (
                  <div key={index} className="flex items-center text-sm text-gray-600">
                    <div className="h-1.5 w-1.5 bg-blue-500 rounded-full mr-2" />
                    {step}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {isEditing ? (
          /* Edit Mode */
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Profile Pictures Management */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Camera className="h-5 w-5 mr-2" />
                Profile Pictures
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Upload multiple profile pictures and choose your primary avatar. You can have up to 5 profile pictures.
              </p>
              
              <ImageUploadComponent
                {...UPLOAD_PRESETS.USER_AVATAR}
                multiple={true}
                maxFiles={5}
                entityType="provider_avatar"
                entityId={provider?.provider_id}
                allowPrimary={true}
                allowCaptions={false}
                initialImages={avatarImages}
                onUpload={handleAvatarUpload}
                onDelete={handleAvatarDelete}
                onSetPrimary={handleAvatarSetPrimary}
                onChange={setAvatarImages}
                className="w-full"
                dragDropText="Drop profile pictures here or click to browse"
                browseText="Add Profile Pictures"
              />
            </div>

            {/* Business Gallery */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Images className="h-5 w-5 mr-2" />
                Business Gallery
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Showcase your work and business with up to 15 high-quality images. The first image will be your featured gallery image.
              </p>
              
              <ImageUploadComponent
                {...UPLOAD_PRESETS.PROVIDER_GALLERY}
                entityType="provider"
                entityId={provider?.provider_id}
                initialImages={galleryImages}
                onUpload={handleGalleryUpload}
                onDelete={handleGalleryDelete}
                onUpdateCaption={handleCaptionUpdate}
                onSetPrimary={handleSetPrimary}
                onChange={setGalleryImages}
                className="w-full"
                dragDropText="Drop images here or click to browse"
                browseText="Add Gallery Images"
              />
            </div>

            {/* Business Information */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-6 flex items-center">
                <Building className="h-5 w-5 mr-2" />
                Business Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    value={formData.business_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                    className={`input ${errors.business_name ? 'border-red-500' : ''}`}
                    placeholder="Your business name"
                  />
                  {errors.business_name && (
                    <p className="text-sm text-red-600 mt-1">{errors.business_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Type *
                  </label>
                  <select
                    value={formData.business_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_type: e.target.value as any }))}
                    className="input"
                  >
                    <option value="individual">Individual</option>
                    <option value="small_business">Small Business</option>
                    <option value="company">Company</option>
                    <option value="llc">LLC</option>
                    <option value="corporation">Corporation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Phone *
                  </label>
                  <input
                    type="tel"
                    value={formData.business_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_phone: e.target.value }))}
                    className={`input ${errors.business_phone ? 'border-red-500' : ''}`}
                    placeholder="+1 (555) 123-4567"
                  />
                  {errors.business_phone && (
                    <p className="text-sm text-red-600 mt-1">{errors.business_phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Email *
                  </label>
                  <input
                    type="email"
                    value={formData.business_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_email: e.target.value }))}
                    className={`input ${errors.business_email ? 'border-red-500' : ''}`}
                    placeholder="business@example.com"
                  />
                  {errors.business_email && (
                    <p className="text-sm text-red-600 mt-1">{errors.business_email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Experience *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={formData.years_experience}
                    onChange={(e) => setFormData(prev => ({ ...prev, years_experience: parseInt(e.target.value) || 1 }))}
                    className={`input ${errors.years_experience ? 'border-red-500' : ''}`}
                  />
                  {errors.years_experience && (
                    <p className="text-sm text-red-600 mt-1">{errors.years_experience}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    className={`input ${errors.website ? 'border-red-500' : ''}`}
                    placeholder="https://your-website.com"
                  />
                  {errors.website && (
                    <p className="text-sm text-red-600 mt-1">{errors.website}</p>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Description *
                </label>
                <textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className={`input ${errors.description ? 'border-red-500' : ''}`}
                  placeholder="Describe your business and services..."
                />
                {errors.description && (
                  <p className="text-sm text-red-600 mt-1">{errors.description}</p>
                )}
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About You *
                </label>
                <textarea
                  rows={4}
                  value={formData.about_me}
                  onChange={(e) => setFormData(prev => ({ ...prev, about_me: e.target.value }))}
                  className={`input ${errors.about_me ? 'border-red-500' : ''}`}
                  placeholder="Tell customers about your experience and expertise..."
                />
                {errors.about_me && (
                  <p className="text-sm text-red-600 mt-1">{errors.about_me}</p>
                )}
              </div>
            </div>

            {/* Location Information */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-6 flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Location Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    className={`input ${errors.address ? 'border-red-500' : ''}`}
                    placeholder="Street address"
                  />
                  {errors.address && (
                    <p className="text-sm text-red-600 mt-1">{errors.address}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className={`input ${errors.city ? 'border-red-500' : ''}`}
                    placeholder="City"
                  />
                  {errors.city && (
                    <p className="text-sm text-red-600 mt-1">{errors.city}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State/Province
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                    className={`input ${errors.state ? 'border-red-500' : ''}`}
                    placeholder="State or Province"
                  />
                  {errors.state && (
                    <p className="text-sm text-red-600 mt-1">{errors.state}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                    className={`input ${errors.postal_code ? 'border-red-500' : ''}`}
                    placeholder="Postal code"
                  />
                  {errors.postal_code && (
                    <p className="text-sm text-red-600 mt-1">{errors.postal_code}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                    className={`input ${errors.country ? 'border-red-500' : ''}`}
                    placeholder="Country"
                  />
                  {errors.country && (
                    <p className="text-sm text-red-600 mt-1">{errors.country}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Professional Details */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-6 flex items-center">
                <Award className="h-5 w-5 mr-2" />
                Professional Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specializations
                  </label>
                  <textarea
                    rows={3}
                    value={formData.specializations}
                    onChange={(e) => setFormData(prev => ({ ...prev, specializations: e.target.value }))}
                    className={`input ${errors.specializations ? 'border-red-500' : ''}`}
                    placeholder="List your specializations (comma-separated)"
                  />
                  {errors.specializations && (
                    <p className="text-sm text-red-600 mt-1">{errors.specializations}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certifications
                  </label>
                  <textarea
                    rows={3}
                    value={formData.certifications}
                    onChange={(e) => setFormData(prev => ({ ...prev, certifications: e.target.value }))}
                    className={`input ${errors.certifications ? 'border-red-500' : ''}`}
                    placeholder="List your certifications (comma-separated)"
                  />
                  {errors.certifications && (
                    <p className="text-sm text-red-600 mt-1">{errors.certifications}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business License
                  </label>
                  <input
                    type="text"
                    value={formData.business_license}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_license: e.target.value }))}
                    className={`input ${errors.business_license ? 'border-red-500' : ''}`}
                    placeholder="License number or details"
                  />
                  {errors.business_license && (
                    <p className="text-sm text-red-600 mt-1">{errors.business_license}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Insurance Details
                  </label>
                  <input
                    type="text"
                    value={formData.insurance_details}
                    onChange={(e) => setFormData(prev => ({ ...prev, insurance_details: e.target.value }))}
                    className={`input ${errors.insurance_details ? 'border-red-500' : ''}`}
                    placeholder="Insurance provider and details"
                  />
                  {errors.insurance_details && (
                    <p className="text-sm text-red-600 mt-1">{errors.insurance_details}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile URL Slug
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 text-sm mr-2">atmyarea.com/provider/</span>
                    {provider?.provider_slug_url ? (
                      <div className="flex-1">
                        <input
                          type="text"
                          value={formData.provider_slug_url}
                          disabled
                          className="input flex-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                          placeholder="your-business-name"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Profile URL is already set and cannot be changed.
                        </p>
                      </div>
                    ) : (
                      <div className="flex-1">
                        <input
                          type="text"
                          value={formData.provider_slug_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, provider_slug_url: e.target.value }))}
                          className={`input flex-1 ${errors.provider_slug_url ? 'border-red-500' : ''}`}
                          placeholder="your-business-name"
                        />
                        {errors.provider_slug_url && (
                          <p className="text-sm text-red-600 mt-1">{errors.provider_slug_url}</p>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          This will be your public profile URL. Once set, it cannot be changed.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Personal Information */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-6 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    className={`input ${errors.first_name ? 'border-red-500' : ''}`}
                    placeholder="Your first name"
                  />
                  {errors.first_name && (
                    <p className="text-sm text-red-600 mt-1">{errors.first_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    className={`input ${errors.last_name ? 'border-red-500' : ''}`}
                    placeholder="Your last name"
                  />
                  {errors.last_name && (
                    <p className="text-sm text-red-600 mt-1">{errors.last_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className={`input ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && (
                    <p className="text-sm text-red-600 mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                    className={`input ${errors.phone_number ? 'border-red-500' : ''}`}
                    placeholder="+1 (555) 123-4567"
                  />
                  {errors.phone_number && (
                    <p className="text-sm text-red-600 mt-1">{errors.phone_number}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Language
                  </label>
                  <select
                    value={formData.preferred_language}
                    onChange={(e) => setFormData(prev => ({ ...prev, preferred_language: e.target.value }))}
                    className="input"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                    <option value="pt">Portuguese</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Submit buttons */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setErrors({});
                }}
                className="btn-secondary flex items-center"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdating}
                className="btn-primary flex items-center"
              >
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </button>
            </div>
          </form>
        ) : (
          /* View Mode */
          <div className="space-y-8">
            {/* Profile Overview */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    {(() => {
                      // Use primary avatar from avatar gallery if available
                      const primaryAvatar = avatarImages.find(img => img.isPrimary);
                      if (primaryAvatar) {
                        return (
                          <img 
                            src={primaryAvatar.url}
                            alt="Profile" 
                            className="h-full w-full object-cover"
                          />
                        );
                      }
                      // Fallback to any avatar if no primary is set
                      if (avatarImages.length > 0) {
                        return (
                          <img 
                            src={avatarImages[0].url}
                            alt="Profile" 
                            className="h-full w-full object-cover"
                          />
                        );
                      }
                      // Fallback to legacy profile picture
                      if (provider?.profile_picture) {
                        return (
                          <img 
                            src={`/media/serve/${provider.profile_picture}`}
                            alt="Profile" 
                            className="h-full w-full object-cover"
                          />
                        );
                      }
                      // Default icon
                      return <User className="h-12 w-12 text-gray-400" />;
                    })()}
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {provider?.business_name || 'Business Name Not Set'}
                  </h2>
                  <p className="text-gray-600 mt-1 capitalize">
                    {provider?.business_type?.replace('_', ' ') || 'Business Type Not Set'}
                  </p>
                  <div className="flex items-center mt-2 space-x-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {provider?.years_experience || 0} years experience
                    </div>
                    {provider?.is_verified === '1' && (
                      <div className="flex items-center text-sm text-green-600">
                        <Shield className="h-4 w-4 mr-1" />
                        Verified
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Business Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Information */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Mail className="h-5 w-5 mr-2" />
                  Contact Information
                </h3>
                <div className="space-y-4">
                  {provider?.business_email && (
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-3" />
                      <span className="text-gray-900">{provider.business_email}</span>
                    </div>
                  )}
                  {provider?.phone_number && (
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 text-gray-400 mr-3" />
                      <span className="text-gray-900">{provider.phone_number}</span>
                    </div>
                  )}
                  {provider?.website && (
                    <div className="flex items-center">
                      <Globe className="h-4 w-4 text-gray-400 mr-3" />
                      <a 
                        href={provider.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        {provider.website}
                      </a>
                    </div>
                  )}
                  {provider?.address && (
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 text-gray-400 mr-3" />
                      <span className="text-gray-900">
                        {provider.address}
                        {provider.city && `, ${provider.city}`}
                        {provider.state && `, ${provider.state}`}
                        {provider.postal_code && ` ${provider.postal_code}`}
                        {provider.country && `, ${provider.country}`}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Professional Details */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Award className="h-5 w-5 mr-2" />
                  Professional Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Services Count</p>
                    <p className="text-gray-900">{provider?.services_count || 0} services</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Service Areas</p>
                    <p className="text-gray-900">{provider?.areas_count || 0} areas</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Rating</p>
                    <p className="text-gray-900">{provider?.rating || 'No rating'} ⭐</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Total Reviews</p>
                    <p className="text-gray-900">{provider?.total_reviews || 0} reviews</p>
                  </div>
                  {provider?.specializations && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Specializations</p>
                      <p className="text-gray-900">{provider.specializations}</p>
                    </div>
                  )}
                  {provider?.certifications && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Certifications</p>
                      <p className="text-gray-900">{provider.certifications}</p>
                    </div>
                  )}
                  {provider?.business_license && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Business License</p>
                      <p className="text-gray-900">{provider.business_license}</p>
                    </div>
                  )}
                  {provider?.insurance_details && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Insurance Details</p>
                      <p className="text-gray-900">{provider.insurance_details}</p>
                    </div>
                  )}
                  {provider?.provider_slug_url && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Profile URL</p>
                      <p className="text-blue-600">atmyarea.com/provider/{provider.provider_slug_url}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Pictures Gallery */}
            {avatarImages.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Camera className="h-5 w-5 mr-2" />
                  Profile Pictures
                </h3>
                <div className="flex flex-wrap gap-4">
                  {avatarImages.map((image) => (
                    <div key={image.id} className="relative group">
                      <div className="w-20 h-20 rounded-full bg-gray-100 overflow-hidden border-2 border-gray-200">
                        <img
                          src={image.url}
                          alt="Profile option"
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        />
                      </div>
                      
                      {/* Primary badge */}
                      {image.isPrimary && (
                        <div className="absolute -top-1 -right-1">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium bg-blue-500 text-white">
                            <Award className="h-3 w-3" />
                          </span>
                        </div>
                      )}
                      
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity rounded-full">
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => window.open(image.url, '_blank')}
                            className="bg-white text-gray-900 px-2 py-1 rounded text-xs font-medium hover:bg-gray-100 transition-colors"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-500">
                    {avatarImages.length} profile picture{avatarImages.length !== 1 ? 's' : ''} available
                  </p>
                </div>
              </div>
            )}

            {/* Business Gallery */}
            {galleryImages.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Images className="h-5 w-5 mr-2" />
                  Business Gallery
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {galleryImages.map((image) => (
                    <div key={image.id} className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={image.url}
                        alt={image.caption || 'Gallery image'}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      
                      {/* Primary badge */}
                      {image.isPrimary && (
                        <div className="absolute top-2 left-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500 text-white">
                            <Award className="h-3 w-3 mr-1" />
                            Featured
                          </span>
                        </div>
                      )}
                      
                      {/* Caption overlay */}
                      {image.caption && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm">
                          <p className="truncate">{image.caption}</p>
                        </div>
                      )}
                      
                      {/* Hover overlay with view action */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity">
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => window.open(image.url, '_blank')}
                            className="bg-white text-gray-900 px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
                          >
                            View Full Size
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {galleryImages.length > 0 && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-500">
                      {galleryImages.length} image{galleryImages.length !== 1 ? 's' : ''} in gallery
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {provider?.description && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4">Business Description</h3>
                <p className="text-gray-700 leading-relaxed">{provider.description}</p>
              </div>
            )}

            {/* About */}
            {provider?.about_me && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4">About</h3>
                <p className="text-gray-700 leading-relaxed">{provider.about_me}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessProfile;